import { useEffect, useState } from "react";
import { Undo2, Redo2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { undoLast, redoLast, undoCount, redoCount, peekUndo, peekUndoAge, peekRedo, subscribeUndo } from "@/undo";

const short = (s) => (s && s.length > 18 ? s.slice(0, 17) + "…" : s || "");
const OLD_MS = 5 * 60 * 1000;

export default function UndoButton({ floating, dark }) {
  const { t } = useI18n();
  const [, force] = useState(0);
  const [busy, setBusy] = useState(false);
  useEffect(() => subscribeUndo(() => force((x) => x + 1)), []);
  useEffect(() => {
    const id = setInterval(() => force((x) => x + 1), 30000);
    return () => clearInterval(id);
  }, []);
  const n = undoCount(), rn = redoCount();

  const fmtAge = (ms) => {
    if (ms == null || ms < 60000) return t("undoJustNow");
    const m = Math.floor(ms / 60000);
    if (m < 60) return `${m} min`;
    return `${Math.floor(m / 60)} h ${m % 60} min`;
  };

  const run = async (fn, msgKey) => {
    if (busy) return;
    setBusy(true);
    try {
      const label = await fn();
      if (label) toast.success(`${t(msgKey)}: ${label}`);
      window.dispatchEvent(new Event("focus"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("undoFailed"));
    } finally { setBusy(false); }
  };

  const askUndo = () => {
    const age = peekUndoAge();
    if (age != null && age > OLD_MS) {
      toast(`${t("undoOldAsk").replace("{t}", fmtAge(age))}: “${peekUndo()}”`, {
        action: { label: t("undoConfirmBtn"), onClick: () => run(undoLast, "undoneMsg") },
        duration: 8000,
      });
      return;
    }
    run(undoLast, "undoneMsg");
  };

  if (floating && n === 0 && rn === 0) return null;

  if (!floating) {
    const hov = dark ? "hover:bg-white/10" : "hover:bg-muted";
    return (
      <span className="flex items-center">
        <button onClick={askUndo} disabled={n === 0 || busy}
          title={n ? `${t("undoBtn")}: ${peekUndo()} · ${fmtAge(peekUndoAge())}` : t("undoEmpty")}
          className={`relative p-1.5 rounded-lg ${hov} disabled:opacity-30`} data-testid={dark ? "undo-btn-sidebar" : "undo-btn-mobile"}>
          <Undo2 className={`w-4 h-4 ${dark ? "text-white/70" : ""}`} />
          {n > 0 && <span className="absolute -top-1 -right-1 bg-accent text-accent-foreground text-[9px] mono rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5" data-testid="undo-count">{n}</span>}
        </button>
        <button onClick={() => run(redoLast, "redoneMsg")} disabled={rn === 0 || busy}
          title={rn ? `${t("redoBtn")}: ${peekRedo()}` : t("redoBtn")}
          className={`relative p-1.5 rounded-lg ${hov} disabled:opacity-30`} data-testid={dark ? "redo-btn-sidebar" : "redo-btn-mobile"}>
          <Redo2 className={`w-4 h-4 ${dark ? "text-white/70" : ""}`} />
          {rn > 0 && <span className="absolute -top-1 -right-1 bg-accent text-accent-foreground text-[9px] mono rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5" data-testid="redo-count">{rn}</span>}
        </button>
      </span>
    );
  }

  return (
    <span className="flex items-center gap-1.5">
      <button onClick={askUndo} disabled={n === 0 || busy}
        title={n ? `${t("undoBtn")}: ${peekUndo()} · ${fmtAge(peekUndoAge())}` : t("undoEmpty")}
        className="flex items-center gap-1.5 bg-white/90 backdrop-blur border border-border shadow-sm rounded-full px-3.5 py-1.5 text-sm font-medium hover:bg-muted transition-colors disabled:opacity-40"
        data-testid="undo-btn-desktop">
        <Undo2 className="w-4 h-4" />
        <span>{t("undoBtn")}{n > 0 && <span className="text-muted-foreground font-normal"> · {short(peekUndo())} · {fmtAge(peekUndoAge())}</span>}</span>
        {n > 0 && <span className="mono text-[10px] bg-accent/15 text-accent rounded-full px-1.5" data-testid="undo-count">{n}</span>}
      </button>
      {rn > 0 && (
        <button onClick={() => run(redoLast, "redoneMsg")} disabled={busy}
          title={`${t("redoBtn")}: ${peekRedo()}`}
          className="flex items-center gap-1 bg-white/90 backdrop-blur border border-border shadow-sm rounded-full px-2.5 py-1.5 text-sm font-medium hover:bg-muted transition-colors"
          data-testid="redo-btn-desktop">
          <Redo2 className="w-4 h-4" />
          <span>{t("redoBtn")}<span className="text-muted-foreground font-normal"> · {short(peekRedo())}</span></span>
          <span className="mono text-[10px] bg-accent/15 text-accent rounded-full px-1.5" data-testid="redo-count">{rn}</span>
        </button>
      )}
    </span>
  );
}
